import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Send } from 'lucide-react';
import { getApiUrl } from '../../utils/api';

const API_URL = getApiUrl();

// LMS V52 §2.2/§2.3 — the rep's answer lands inside her SoulAI chat and her reply comes back
// here as a `support_message` event. One thread per ticket.
export const RequestThread = ({ request, onSent }) => {
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const messages = request.messages || [];

  const send = async () => {
    const message = text.trim();
    if (!message) return;
    setSending(true);
    try {
      await axios.post(`${API_URL}/api/leads/requests/${request.request_id}/reply`, { message },
        { withCredentials: true });
      toast.success('Sent into her SoulAI chat');
      setText('');
      onSent?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send the reply');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-slate-50 px-4 py-3" data-testid={`request-thread-${request.request_id}`}>
      <div className="space-y-2 max-h-56 overflow-y-auto">
        {messages.length === 0 && (
          <p className="text-xs text-slate-500">No messages yet. Your reply opens the conversation in her SoulAI chat.</p>
        )}
        {messages.map((m, i) => {
          const fromTeam = m.direction === 'team_to_learner';
          return (
            <div key={m.message_id || i} className={`flex ${fromTeam ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-lg rounded-2xl px-3 py-2 text-xs ${fromTeam
                ? 'bg-emerald-600 text-white rounded-br-sm'
                : 'bg-white border border-slate-200 text-slate-700 rounded-bl-sm'}`}
                data-testid={`request-message-${fromTeam ? 'team' : 'learner'}-${i}`}>
                <p className="whitespace-pre-wrap">{m.message}</p>
                <p className={`mt-1 text-[10px] ${fromTeam ? 'text-emerald-100' : 'text-slate-400'}`}>
                  {fromTeam ? `Soul Sanskar team${m.agent_name ? ` · ${m.agent_name}` : ''}` : 'She wrote'}
                  {m.at ? ` · ${new Date(m.at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}` : ''}
                </p>
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-3 flex items-end gap-2">
        <textarea
          rows={2}
          maxLength={2000}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Write to her — this appears in her SoulAI chat and she can reply"
          className="input-base flex-1 resize-none text-sm"
          data-testid={`request-reply-input-${request.request_id}`}
        />
        <button
          type="button"
          onClick={send}
          disabled={sending || !text.trim()}
          className="btn-primary inline-flex items-center gap-1 text-xs py-2 disabled:opacity-50"
          data-testid={`request-reply-send-${request.request_id}`}
        >
          <Send className="w-3.5 h-3.5" /> {sending ? 'Sending…' : 'Send'}
        </button>
      </div>
    </div>
  );
};
